import {difference} from 'lodash-es';
import {useCallback, useEffect, useRef} from 'react';
import Icon, {IconSize} from '@/components/Icon';
import toast, {ToastType, toastObjects} from '@/components/Toast';
import {EDITOR_MODE} from '@/Constant';
import {CloseSvg} from '@/icons';
import {INewVersionInfo} from '@/interface';
import sdk from '@/sdk';
import * as store from '@/store/index';
import {CollaboratorList} from '@/types/document/collaborator';


interface NewVersionInfoProps {
    curDocNewVersionInfo?: INewVersionInfo;
    mode?: EDITOR_MODE;
    curDocCollaborators?: CollaboratorList[];
    curDocument?: WizDocument;
    onRefresh?: () => void;
}

function removeToast() {
    toastObjects[ToastType.Error]?.remove();
    toastObjects[ToastType.Warning]?.remove();
}


export const NewVersionInfo = ({
    mode,
    curDocNewVersionInfo,
    curDocument,
    curDocCollaborators = [],
    onRefresh,
}: NewVersionInfoProps) => {

    const cacheInfo = useRef(curDocNewVersionInfo);

    const handleClickRefresh = useCallback(() => {
        onRefresh && onRefresh();
        removeToast();
    }, [onRefresh]);

    const showToast = useCallback(() => {
        const {
            uids,
            docGuid,
        } = curDocNewVersionInfo || {};

        function previewModeToast() {
            if (!uids || !uids.length) {
                return;
            }
            const isCurUser = uids?.some(val => {
                return val === sdk.as.user?.userGuid;
            });
            if (
                docGuid === curDocument?.docGuid
                && !isCurUser
            ) {
                removeToast();
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                toast({
                    type: ToastType.Warning,
                    className: 'previewTip',
                    duration: -1,
                    content: (
                        <span>
                            有用户更新了文档 <a onClick={handleClickRefresh}>立即刷新</a>
                            <Icon
                                svg={CloseSvg}
                                size={IconSize.XS}
                                onClick={removeToast}
                                style={{marginLeft: 8, verticalAlign: 'baseline'}}
                            />
                        </span>
                    ),
                });
            }
        }

        function editModeToast() {
            if (!uids || !uids.length) {
                return;
            }

            const toastMembers = difference(curDocCollaborators.map(data => data.uid), uids);
            if (docGuid === curDocument?.docGuid) {
                toastMembers.forEach(data => {
                    if (sdk.as.user?.userGuid === data) {
                        removeToast();
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        toast({
                            type: ToastType.Warning,
                            className: 'editTip',
                            duration: 3000,
                            content: (
                                <span>有用户发布了页面，你的内容已同时发布</span>
                            ),
                            manualClose: true,
                        });
                    }
                });
            }
        }

        if (mode === EDITOR_MODE.preview) {
            previewModeToast();
            return;
        }
        if (mode === EDITOR_MODE.edit) {
            editModeToast();
        }
    }, [curDocCollaborators, curDocNewVersionInfo, curDocument?.docGuid, handleClickRefresh, mode]);

    useEffect(() => {
        if (cacheInfo.current === curDocNewVersionInfo) {
            return;
        }
        cacheInfo.current = curDocNewVersionInfo;
        showToast();
    }, [curDocNewVersionInfo, showToast]);

    useEffect(() => {
        return () => {
            removeToast();
        };
    }, [curDocument?.docGuid, mode]);

    return null;
};

export default store.connect([
    store.KEYS.CUR_DOCUMENT_NEW_VERSION_INFO,
    store.KEYS.CUR_DOCUMENT_COLLABORATORS,
])(NewVersionInfo);

